import clsx from 'clsx';
import { Suspense, useCallback, useMemo } from 'react';

import { useCardFormat } from './hooks/useCardFormat';
import { getComponent, useRelevancyFeed } from './model';
import { useRelevancyOptions } from './store';
import type { RelevancyCard, RelevancyCardType, RelevancyCardFormat } from './types';

import ErrorBoundary from '@shared/ErrorBoundary';
import Loader from '@shared/Loader';
import { IS_DEBUG } from '@utilities/browser';

export type RelevancyCardProps = {
  item?: RelevancyCard;
  index?: number;
  cardFormat?: RelevancyCardFormat;
};

export const CardPlaceholder = ({ loader, type }: { loader?: boolean; type?: RelevancyCardType }) => (
  <div className='skl-card pk-position--relative'>
    {loader && <Loader />}
    {IS_DEBUG && type}
  </div>
);

export const DynamicCardComponent = ({
  item,
  index,
  items,
}: {
  item: RelevancyCard;
  index: number;
  items: RelevancyCard[];
}) => {
  const { setCardError } = useRelevancyFeed();
  const {
    options: { isPortrait, isSquare, isOver, design, wideCardIndex, wideCardPeriod, cardsIndexSpanPairs, Components },
  } = useRelevancyOptions();

  const cardsSpan = useMemo(() => new URLSearchParams(cardsIndexSpanPairs), [cardsIndexSpanPairs]);
  const { cardFormat } = useCardFormat({
    isPortrait,
    isSquare,
    isOver,
    design,
    wideCardIndex,
    wideCardPeriod,
    cardsSpan,
    cardsNumber: items.length,
    currentCardIndex: index,
  });
  const Tag = useMemo(() => getComponent<RelevancyCardProps>(item?.type, Components), [item?.type, Components]);

  const handleCardError = useCallback(
    (error, info) => {
      setCardError(item, error, info);
    },
    [item, setCardError],
  );

  return (
    <div
      className={clsx(
        cardFormat,
        item.hidden && 'pk-d--none',
        cardsSpan.has(`${index}`) && `col-span-${cardsSpan.get(`${index}`)}`,
      )}
    >
      {Tag && cardFormat ? (
        <ErrorBoundary onComponentError={handleCardError}>
          <Suspense fallback={<CardPlaceholder loader />}>
            <Tag item={item} index={index} cardFormat={cardFormat} />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <CardPlaceholder type={item?.type} />
      )}
    </div>
  );
};

export default DynamicCardComponent;
